import { io } from 'socket.io-client'
import { resolveApiUrl } from './resolveApiUrl.js'

class WebSocketService {
  constructor() {
    this.socket = null
    this.connected = false
    this.reconnecting = false
    this.listeners = new Map()
  }

  /**
   * Initialiser la connexion WebSocket
   */
  connect(token) {
    if (this.socket?.connected) {
      console.log('WebSocket already connected')
      return this.socket
    }

    this.socket = io(`${resolveApiUrl()}/notifications`, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      transports: ['websocket', 'polling']
    })

    this.setupEventHandlers()
    return this.socket
  }

  /**
   * Configurer les gestionnaires d'événements
   */
  setupEventHandlers() {
    if (!this.socket) return

    // Événements de connexion
    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected:', this.socket.id)
      this.connected = true
      this.reconnecting = false
      this.emit('connection:success')
    })

    this.socket.on('connected', (data) => {
      console.log('✅ Server confirmed connection:', data)
      this.emit('server:connected', data)
    })

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason)
      this.connected = false
      this.emit('connection:lost', { reason })
    })

    this.socket.on('connect_error', (error) => {
      console.error('❌ Connection error:', error.message)
      this.emit('connection:error', { error: error.message })
    })

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Reconnected after', attemptNumber, 'attempts')
      this.reconnecting = false
      this.emit('connection:reconnected', { attemptNumber })
    })

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Reconnection attempt', attemptNumber)
      this.reconnecting = true
      this.emit('connection:reconnecting', { attemptNumber })
    })

    this.socket.on('reconnect_error', (error) => {
      console.error('❌ Reconnection error:', error.message)
    })

    this.socket.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed')
      this.emit('connection:failed')
    })

    // ===== ÉVÉNEMENTS DE NOTIFICATIONS =====
    
    // Initialisation des notifications (reçu à la connexion)
    this.socket.on('notifications:init', (data) => {
      console.log('📬 Notifications initialized:', data)
      this.emit('notifications:init', data)
    })

    // Compteur de notifications non lues
    this.socket.on('notifications:count', (data) => {
      console.log('🔢 Notifications count:', data)
      this.emit('notifications:count', data)
    })

    // Nouvelle notification
    this.socket.on('notification:new', (data) => {
      console.log('🔔 New notification:', data)
      this.emit('notification:new', data)
    })

    // Notification marquée comme lue
    this.socket.on('notification:read', (data) => {
      console.log('✅ Notification marked as read:', data)
      this.emit('notification:read', data)
    })

    // Toutes les notifications marquées comme lues
    this.socket.on('notifications:all_read', (data) => {
      console.log('✅ All notifications marked as read')
      this.emit('notifications:all_read', data)
    })

    // Événements de présence
    this.socket.on('user:online', (data) => {
      console.log('👤 User online:', data.userId)
      this.emit('user:online', data)
    })

    this.socket.on('user:offline', (data) => {
      console.log('👤 User offline:', data.userId)
      this.emit('user:offline', data)
    })

    // Ping/Pong
    this.socket.on('pong', (data) => {
      console.log('🏓 Pong received:', data)
    })
  }

  /**
   * Se déconnecter
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.connected = false
      this.listeners.clear()
      console.log('WebSocket disconnected')
    }
  }

  /**
   * Écouter un événement
   */
  on(event, callback) {
    if (!this.socket) {
      console.warn('WebSocket not connected. Event listener not added:', event)
      return
    }

    // Stocker le callback pour pouvoir le retirer plus tard
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event).push(callback)

    this.socket.on(event, callback)
  }

  /**
   * Retirer un écouteur d'événement
   */
  off(event, callback) {
    if (!this.socket) return

    if (callback) {
      this.socket.off(event, callback)
      
      // Retirer du registre
      const callbacks = this.listeners.get(event) || []
      const index = callbacks.indexOf(callback)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
    } else {
      // Retirer tous les écouteurs pour cet événement
      this.socket.off(event)
      this.listeners.delete(event)
    }
  }

  /**
   * Écouter un événement une seule fois
   */
  once(event, callback) {
    if (!this.socket) {
      console.warn('WebSocket not connected. Event listener not added:', event)
      return
    }

    this.socket.once(event, callback)
  }

  /**
   * Émettre un événement
   */
  emit(event, data) {
    if (!this.socket) {
      console.warn('WebSocket not connected. Cannot emit:', event)
      return
    }

    this.socket.emit(event, data)
  }

  /**
   * Envoyer un ping
   */
  ping() {
    this.emit('ping')
  }

  /**
   * Marquer une notification comme lue
   */
  markAsRead(notificationId) {
    this.emit('notification:read', { notificationId })
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  markAllAsRead() {
    this.emit('notifications:mark_all_read')
  }

  /**
   * Demander le compteur de notifications
   */
  requestNotificationCount() {
    this.emit('notifications:get_count')
  }

  /**
   * Demander toutes les notifications
   */
  requestNotifications() {
    this.emit('notifications:get_all')
  }

  /**
   * Vérifier si connecté
   */
  isConnected() {
    return this.connected && this.socket?.connected
  }

  /**
   * Obtenir l'ID de la socket
   */
  getSocketId() {
    return this.socket?.id
  }

  /**
   * Écouter tous les événements de notification
   */
  onNotification(callback) {
    const events = [
      // Alertes
      'alert:created',
      'alert:updated',
      
      // Stock
      'stock:low',
      'stock:critical',
      'stock:out',
      'stock:updated',
      
      // Commandes
      'order:created',
      'order:approved',
      'order:received',
      'order:status_changed',
      
      // Transferts
      'transfer:requested',
      'transfer:approved',
      'transfer:rejected',
      'transfer:completed',
      
      // Équipements
      'equipment:maintenance_due',
      'equipment:out_of_order',
      'equipment:status_changed',
      
      // Produits
      'product:expiring',
      'product:expired',
      
      // Système
      'system:message',
      'user:mentioned'
    ]

    events.forEach(event => {
      this.on(event, (data) => {
        callback({ event, data })
      })
    })
  }

  /**
   * Retirer tous les écouteurs de notification
   */
  offNotification() {
    const events = [
      'alert:created', 'alert:updated',
      'stock:low', 'stock:critical', 'stock:out', 'stock:updated',
      'order:created', 'order:approved', 'order:received', 'order:status_changed',
      'transfer:requested', 'transfer:approved', 'transfer:rejected', 'transfer:completed',
      'equipment:maintenance_due', 'equipment:out_of_order', 'equipment:status_changed',
      'product:expiring', 'product:expired',
      'system:message', 'user:mentioned'
    ]

    events.forEach(event => {
      this.off(event)
    })
  }
}

// Instance singleton
const websocketService = new WebSocketService()

export default websocketService
